import { ref, computed } from 'vue';
import type {
  IframeEntry,
  IframePlacement,
  IframeRect,
  IframeSpec,
} from './types';

const entries = ref<Map<string, IframeEntry>>(new Map());
const activeKeys = ref<Set<string>>(new Set());

export interface ClaimOptions {
  tabFullPath: string;
  nonce?: number;
  maxLive?: number;
  placement?: IframePlacement;
}

function touch(key: string) {
  const entry = entries.value.get(key);
  if (entry) {
    entry.lastActiveAt = Date.now();
  }
}

function enforceMaxLive(maxLive: number | undefined, protectKey: string) {
  if (!maxLive || maxLive <= 0) {
    return;
  }
  while (entries.value.size > maxLive) {
    let victimKey: string | null = null;
    let oldest = Infinity;
    for (const [key, entry] of entries.value) {
      if (key === protectKey || activeKeys.value.has(key)) {
        continue;
      }
      if (entry.lastActiveAt < oldest) {
        oldest = entry.lastActiveAt;
        victimKey = key;
      }
    }
    if (!victimKey) {
      break;
    }
    entries.value.delete(victimKey);
  }
}

export function useIframeKeepAlive() {
  function claim(key: string, spec: IframeSpec, options: ClaimOptions) {
    const existing = entries.value.get(key);
    if (existing) {
      existing.params = spec.params;
      existing.tabFullPath = options.tabFullPath;
      existing.lastActiveAt = Date.now();
      if (options.placement) {
        existing.placement = options.placement;
      }
      if (options.nonce != null && options.nonce !== existing.nonce) {
        existing.nonce = options.nonce;
      }
    } else {
      entries.value.set(key, {
        ...spec,
        key,
        tabFullPath: options.tabFullPath,
        nonce: options.nonce ?? 0,
        lastActiveAt: Date.now(),
        placement: options.placement ?? 'fill',
        rect: null,
      } as IframeEntry);
    }
    enforceMaxLive(options.maxLive, key);
  }

  function isActive(key: string): boolean {
    return activeKeys.value.has(key);
  }

  function activate(key: string) {
    activeKeys.value.add(key);
    touch(key);
  }

  function deactivate(key: string) {
    activeKeys.value.delete(key);
  }

  function release(key: string) {
    entries.value.delete(key);
    activeKeys.value.delete(key);
  }

  function syncWithTabs(openFullPaths: string[]) {
    const open = new Set(openFullPaths);
    for (const [key, entry] of entries.value) {
      if (!open.has(entry.tabFullPath)) {
        release(key);
      }
    }
  }

  function setRect(key: string, rect: IframeRect | null) {
    const entry = entries.value.get(key);
    if (entry) {
      entry.rect = rect;
    }
  }

  const renderableEntries = computed(() => Array.from(entries.value.values()));

  return {
    entries,
    activeKeys,
    renderableEntries,
    isActive,
    claim,
    activate,
    deactivate,
    setRect,
    release,
    syncWithTabs,
  };
}
